import { TAU } from '../core/math';
import { SFX } from '../platform/audio';
import { STATE, dropChest, spawnRing } from './fx';
import { banner, radio } from '../ui/hud';
import { P } from '../world/player';
/* ============================================================
   怪潮 —— 每五分鐘的密度重音（P5 · 01-run-pacing）
   ============================================================

   `06-POLISH-ROADMAP.md` P5 第 3 條：密度 ×2.5–3、持續 15–25 秒、
   紅光 2 秒 + 音效預警、清完給獎勵。第 5 條的「19:00 收尾危機密度 ×3」
   是同一個機制的最後一次，所以兩條合成這一個檔。

   ---------- 為什麼是「先退潮再壓入」 ----------

   同屏硬上限是 420（`spawn.ts` 的 `ENEMY_CAP`，那是量過的效能決定），
   而基礎目標在第 11.5 分就頂到 300 —— 第 10 分之後的怪潮如果只是
   「把目標值 ×2.6」，天花板會把它切成 ×1.4，玩家根本看不出來。

   所以怪潮分兩段：**先退潮 8 秒（目標 ×0.45）再壓入 18 秒**。
   退潮期間生成停擺，玩家把場上清空（第 15 分的清場速度是每秒 30 幾隻，
   八秒足夠），於是壓入的落差是「幾十隻 → 頂到天花板」——
   體感的 ×2.5 是靠**落差**做出來的，不是靠絕對值。
   附帶的好處是退潮那 8 秒剛好給預告一個安靜的背景。

   ---------- 時刻為什麼不是整五分鐘 ----------

   Boss 在 300 / 540 / 780 / 960 / 1110 秒降臨，而 Boss 戰會把場面
   稀釋到 0.28–0.38（`spawn.ts` 的 `setFieldMul`）—— 怪潮撞上去會被
   直接抵銷掉。所以第一次挪到 4:00，其餘挑 Boss 之間的空檔。 */

/** `[開始秒數, 壓入倍率]`。19:00 那一次是 P5 第 5 條的收尾危機段，倍率最高。 */
export var HORDES: [number, number][] = [
  [240, 2.6],    // 4:00 —— 5:00 是第一隻 Boss，讓開
  [600, 2.8],    // 10:00
  [900, 2.8],    // 15:00
  [1140, 3.4]    // 19:00 —— 收尾危機（roadmap P5 第 5 條）
];

var EBB = 8;      // 退潮秒數
var SURGE = 18;   // 壓入秒數（roadmap 要求 15–25）
var WARN = 2;     // 預告提前量，roadmap 明訂 2 秒

export var HORDE = { mul: 1, i: 0, t: -1, warned: false, count: 0 };

export function resetHordes() {
  HORDE.mul = 1; HORDE.i = 0; HORDE.t = -1; HORDE.warned = false; HORDE.count = 0;
}

/** 預告：八個方位同時壓入，所以標記畫成一整圈而不是一支箭頭 ——
    波次的預告是「從哪邊來」，怪潮的預告是「沒有哪一邊」。 */
function telegraphRing() {
  for (var i = 0; i < 8; i++) {
    var a = i * (TAU / 8);
    spawnRing(P.pos.x + Math.cos(a) * 30, P.pos.z + Math.sin(a) * 30, 0xff2a04, 4.2, 5.8, 1.6);
  }
  SFX.alarm();
}

/** 每個模擬步呼叫一次。回傳值沒有意義 —— 效果透過 `HORDE.mul` 給生成器。 */
export function updateHorde(dt: number) {
  /* 進行中：推進計時，跨過兩個邊界時切換倍率。 */
  if (HORDE.t >= 0) {
    HORDE.t += dt;
    if (HORDE.t < EBB) HORDE.mul = 0.45;
    else if (HORDE.t < EBB + SURGE) {
      if (HORDE.mul < 1) {            // 退潮 → 壓入的那一瞬間
        HORDE.mul = HORDES[HORDE.i][1];
        banner('怪潮 · 全方位壓入');
        radio('密度警報 —— 四面八方，撐過去');
        SFX.alarm();
      }
    } else {
      HORDE.mul = 1; HORDE.t = -1; HORDE.i++; HORDE.count++;
      /* 清完給獎勵（roadmap P5 第 3 條）。沒有獎勵的話玩家學到的是
         「怪潮＝純粹的懲罰」，那正是 08 毒性殺手清單上的東西。

         用**銅箱（三選一）不用銀箱（五選三）**：上一輪的量測已經指出
         寶箱給的選擇次數（27）幾乎跟主線升級一樣多，寶箱從點綴變成第二條主線。
         一場四次怪潮改銀箱就是再加 12 次選擇。要加重再改，先別加。 */
      dropChest(P.pos.x, P.pos.z, false, 'bronze');
      banner('怪潮結束 · 補給投放');
    }
    return;
  }

  if (HORDE.i >= HORDES.length) return;
  var at = HORDES[HORDE.i][0];

  /* 預告。**排在退潮開始之前 2 秒**，所以玩家先聽到警報、才看到場面變空 ——
     反過來的話「變空」會被讀成「這一波打完了」，等於預告了一件錯的事。 */
  if (!HORDE.warned && STATE.t >= at - WARN) {
    HORDE.warned = true;
    telegraphRing();
    banner('警告 · 大規模熱源接近');
    radio('全域偵測到大量集結 —— 準備迎擊');
    return;
  }
  if (STATE.t >= at) { HORDE.t = 0; HORDE.warned = false; }
}
